'use client';

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import {
  fetchNotifications,
  fetchUnreadCount,
  markRead,
  fetchPreferences,
  updatePreferences,
  type NotificationPreferences,
  type NotificationPage,
} from '@/lib/notifications-client';

const KEY_FEED = ['notifications', 'feed'] as const;
const KEY_UNREAD = ['notifications', 'unread'] as const;
const KEY_PREFS = ['notifications', 'preferences'] as const;

export function useNotificationsFeed(opts: { enabled?: boolean } = {}) {
  return useInfiniteQuery({
    queryKey: KEY_FEED,
    enabled: opts.enabled ?? true,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      const res = await fetchNotifications({ cursor: pageParam, limit: 20 });
      return res.data as NotificationPage;
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 30_000,
  });
}

export function useUnreadCount(opts: { enabled?: boolean; refetchIntervalMs?: number } = {}) {
  return useQuery({
    queryKey: KEY_UNREAD,
    enabled: opts.enabled ?? true,
    queryFn: async () => (await fetchUnreadCount()).data.count,
    refetchInterval: opts.refetchIntervalMs ?? 60_000,
    staleTime: 15_000,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids?: number[]; all?: boolean }) => markRead(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_UNREAD });
      qc.invalidateQueries({ queryKey: KEY_FEED });
    },
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: KEY_PREFS,
    queryFn: async () => (await fetchPreferences()).data,
    staleTime: 5 * 60_000,
  });
}

export function useUpdateNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefs: Partial<NotificationPreferences>) => updatePreferences(prefs),
    onSuccess: (res) => {
      qc.setQueryData(KEY_PREFS, res.data);
    },
  });
}

export const NOTIFICATION_QUERY_KEYS = {
  feed: KEY_FEED,
  unread: KEY_UNREAD,
  preferences: KEY_PREFS,
};
